import { type RefObject, useEffect, useRef, useState } from 'react';

interface UseInfiniteScrollOptions {
  hasNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void | Promise<void>;
  rootMargin?: string;
  threshold?: number;
  /** Scroll container for IntersectionObserver; defaults to the viewport. */
  root?: RefObject<Element | null>;
}

export function useInfiniteScroll({
  hasNextPage,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
  threshold = 0,
  root,
}: UseInfiniteScrollOptions) {
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  // Observer reads latest values here so its identity can stay stable.
  const latestRef = useRef({ hasNextPage, isLoading, onLoadMore });
  latestRef.current = { hasNextPage, isLoading, onLoadMore };

  useEffect(() => {
    // LoadMoreSentinel only mounts while hasNextPage is true — wait until then.
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        const latest = latestRef.current;
        if (
          !entry?.isIntersecting ||
          latest.isLoading ||
          loadingMoreRef.current ||
          !latest.hasNextPage
        ) {
          return;
        }

        loadingMoreRef.current = true;
        setIsLoadingMore(true);
        try {
          await latest.onLoadMore();
        } finally {
          loadingMoreRef.current = false;
          setIsLoadingMore(false);
        }
      },
      {
        root: root?.current ?? null,
        rootMargin,
        threshold,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasNextPage, root, rootMargin, threshold]);

  return { sentinelRef: setSentinel, isLoadingMore };
}
